import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { ConfirmProvider } from "./components/ConfirmProvider";

// Pages
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import ClientsPage from "./pages/ClientsPage";
import DocumentsToAssignPage from "./pages/DocumentsToAssignPage";
import PoliciesToConfirmPage from "./pages/PoliciesToConfirmPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import PoliciesPage from "./pages/PoliciesPage";
import PolicyDetailPage from "./pages/PolicyDetailPage";
import CalendarPage from "./pages/CalendarPage";
import CommissionReportPage from "./pages/CommissionReportPage";
import OperationsPage from "./pages/OperationsPage";
import ImportPage from "./pages/ImportPage";
import BackupPage from "./pages/BackupPage";
import GeneralSettingsPage from "./pages/GeneralSettingsPage";
import SignaturePage from "./pages/SignaturePage";
import SignatureManagementPage from "./pages/SignatureManagementPage";
import PortalePage from "./pages/PortalePage";
import EmailPage from "./pages/EmailPage";
import WaTemplatesPage from "./pages/WaTemplatesPage";
import PortfolioPage from "./pages/PortfolioPage";
import RecuperoScadenzePage from "./pages/RecuperoScadenzePage";
import SaraPage from "./pages/SaraPage";
import Layout from "./components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.baseURL = BACKEND_URL;
axios.defaults.withCredentials = true;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  
  // Check for Google OAuth callback with code in query params
  if (location.pathname === '/auth/callback' && location.search?.includes('code=')) {
    return <AuthCallback />;
  }
  
  // Check URL fragment for session_id synchronously during render (Emergent auth)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><Layout><ClientsPage /></Layout></ProtectedRoute>} />
      <Route path="/documenti-da-assegnare" element={<ProtectedRoute><Layout><DocumentsToAssignPage /></Layout></ProtectedRoute>} />
      <Route path="/polizze-da-confermare" element={<ProtectedRoute><Layout><PoliciesToConfirmPage /></Layout></ProtectedRoute>} />
      <Route path="/clients/:clientId" element={<ProtectedRoute><Layout><ClientDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/policies" element={<ProtectedRoute><Layout><PoliciesPage /></Layout></ProtectedRoute>} />
      <Route path="/policies/:policyId" element={<ProtectedRoute><Layout><PolicyDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><Layout><CalendarPage /></Layout></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Layout><CommissionReportPage /></Layout></ProtectedRoute>} />
      <Route path="/operations" element={<ProtectedRoute><Layout><OperationsPage /></Layout></ProtectedRoute>} />
      <Route path="/import" element={<ProtectedRoute><Layout><ImportPage /></Layout></ProtectedRoute>} />
      <Route path="/backup" element={<ProtectedRoute><Layout><BackupPage /></Layout></ProtectedRoute>} />
      <Route path="/general-settings" element={<ProtectedRoute><Layout><GeneralSettingsPage /></Layout></ProtectedRoute>} />
      <Route path="/signatures" element={<ProtectedRoute><Layout><SignatureManagementPage /></Layout></ProtectedRoute>} />
      <Route path="/email" element={<ProtectedRoute><Layout><EmailPage /></Layout></ProtectedRoute>} />
      <Route path="/whatsapp" element={<ProtectedRoute><Layout><WaTemplatesPage /></Layout></ProtectedRoute>} />
      <Route path="/portfolio" element={<ProtectedRoute><Layout><PortfolioPage /></Layout></ProtectedRoute>} />
      <Route path="/recupero-scadenze" element={<ProtectedRoute><Layout><RecuperoScadenzePage /></Layout></ProtectedRoute>} />
      <Route path="/assistant" element={<ProtectedRoute><Layout><SaraPage /></Layout></ProtectedRoute>} />
      {/* Public signature page - no auth required */}
      <Route path="/firma/:token" element={<SignaturePage />} />
      {/* Public client portal - token-scoped, no broker auth */}
      <Route path="/portale/:token" element={<PortalePage />} />
    </Routes>
  );
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip if user passed from AuthCallback
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get('/api/auth/me');
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
}

function App() {
  return (
    <div className="min-h-screen bg-background">
      <BrowserRouter>
        <ConfirmProvider>
          <AppRouter />
        </ConfirmProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
